import React, { useMemo, useState } from 'react'
import { Font } from '../../../../types/Font'
import Text from '../../../../components/Text'
import Input from '../../../../components/Input'
import Button from '../../../../components/Button'
import styles from './EditFontModal.module.css'
import cx from 'classnames'
import {
    faChevronDown,
    faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import Icon from '../../../../components/Icon'
import ToggleSwitch from '../../../../components/ToggleSwitch'
import { useTranslation } from 'react-i18next'
import { useUpdateData } from '../../../../hooks/useUpdateData'
import * as yup from 'yup'
import { ApiRoutes } from '../../../../services/ApiService'
import { closeModal } from '../../../../services/ModalService'
import { showToast } from '../../../../services/ToastService'
import DropInput from '../../../../components/DropInput'

type EditFontModalProps = {
    font: Font
    fonts: Font[]
    onSubmit: () => void
}

const EditFontModal: React.FC<EditFontModalProps> = ({
    font,
    fonts,
    onSubmit,
}) => {
    const [showWebsafe, setShowWebsafe] = useState<boolean>(false)
    const { t } = useTranslation(['settings'])

    const updateFontSchema = useMemo(() => {
        return {
            name: yup
                .string()
                .required()
                .min(3)
                .notOneOf(
                    fonts.filter((f) => f.id !== font.id).map((f) => f.name)
                ),
            active: yup.boolean().required(),
        }
    }, [fonts, font])

    const { element, changeProp, changeFile, submit, isValid, error, files } = useUpdateData<Font>(
        ApiRoutes.FONTS,
        {
            name: font.name,
            active: font.active,
        } as Font,
        font.id,
        updateFontSchema
    )

    const handleSubmit = async () => {
        if (error) {
            showToast(error, 'error')
        } else if (isValid) {
            try {
                const res = await submit()
                if (res.status === 200) {
                    closeModal()
                    showToast(t('fonts.edit_modal.success'))
                    onSubmit()
                } else {
                    showToast(t('fonts.edit_modal.error'), 'error')
                    onSubmit()
                }
            } catch (err: any) {
                if (err.response?.status === 422) {
                    const errorMessage = err.response?.data?.message || t('fonts.edit_modal.error')
                    showToast(errorMessage, 'error')
                } else {
                    showToast(t('fonts.edit_modal.error'), 'error')
                }
                onSubmit()
            }
        }
    }

    return (
        <div className={cx(styles['modal-wrapper'])}>
            <Input
                type={'text'}
                placeholder={t('fonts.labels.name')}
                property={'name'}
                value={element.name}
                onChange={(_, value) => changeProp({ name: value })}
            />
            <div
                className={cx(styles['websafe-header'])}
                onClick={() => setShowWebsafe((prev) => !prev)}
            >
                <Text>{t('fonts.websafe.heading')}</Text>
                <Icon
                    icon={showWebsafe ? faChevronDown : faChevronRight}
                    color={'black'}
                    size={'sm'}
                />
            </div>
            <div
                className={cx(styles['websafe-wrapper'])}
                style={{ maxHeight: !showWebsafe ? '0px' : '100vh' }}
            >
                <Text>{t('fonts.websafe.content')}</Text>
            </div>
            <div className={cx(styles['upload-label'])}>
                {t('fonts.labels.file')}
            </div>
            <div className={'zr-w-100 zr-mb-4'}>
                <DropInput
                    accept={'.ttf,.woff,.woff2'}
                    onFileChange={(file) => changeFile(file)}
                />
            </div>
            {(files.length > 0 || font.path) && (
                <div className={cx(styles['chosen-font'])}>
                    {files.length > 0 
                        ? files[0].name 
                        : font.path 
                            ? font.path.split('/').pop() || font.path
                            : ''}
                </div>
            )}
            <div className={'zr-my-4'}>
                <ToggleSwitch
                    classes={'text-nowrap'}
                    property={'active'}
                    value={element.active}
                    label={t('fonts.labels.active')}
                    onChange={(_, value) => changeProp({ active: value })}
                />
            </div>
            <Button
                hierarchy={'primary'}
                classes={'zr-w-100'}
                text={t('fonts.edit_modal.action')}
                fn={() => handleSubmit()}
                disabled={!isValid}
            />
        </div>
    )
}

export default EditFontModal
